import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FlaskConical, Plus, Play, Pause, Archive, RefreshCw,
  ChevronDown, ChevronUp, Users, X, Trash2,
} from 'lucide-react';
import type { Approval, DestructiveOperation } from '../api/approvalRequests';
import { experimentsApi, type Experiment, type ExperimentVariant } from '../api/experiments';
import ApprovalRequestModal from '../components/ApprovalRequestModal';
import { useToast } from '../components/ui';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-500/20 text-gray-400',
  ACTIVE: 'bg-green-500/20 text-green-400',
  PAUSED: 'bg-yellow-500/20 text-yellow-400',
  ARCHIVED: 'bg-red-500/20 text-red-400',
};

/**
 * DM-6437: activate and archive are FM-105 destructive operations. Each opens
 * the shared approval modal, which runs `execute` once a second operator has
 * approved.
 */
interface PendingApproval {
  operation: DestructiveOperation;
  targetId: string;
  targetLabel: string;
  title: string;
  description?: string;
  execute: (approval: Approval) => Promise<void>;
}

export default function Experiments() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: experiments, isLoading, refetch } = useQuery({
    queryKey: ['experiments', statusFilter],
    queryFn: () => experimentsApi.list(statusFilter || undefined),
  });

  const toast = useToast();
  const [approvalAction, setApprovalAction] = useState<PendingApproval | null>(null);

  function requestActivate(exp: Experiment): void {
    setApprovalAction({
      operation: 'activateExperiment',
      targetId: exp.id,
      targetLabel: exp.name,
      title: `Activate "${exp.name}"`,
      description: `Starts serving variants to ${exp.traffic_percentage}% of traffic.`,
      execute: async approval => {
        await experimentsApi.activate(exp.id, approval);
      },
    });
  }

  // There is no pause state on the API: the Pause button has always archived.
  // The modal says so rather than letting the icon imply something reversible.
  function requestArchive(exp: Experiment, viaPause: boolean): void {
    setApprovalAction({
      operation: 'archiveExperiment',
      targetId: exp.id,
      targetLabel: exp.name,
      title: `Archive "${exp.name}"`,
      description: viaPause
        ? 'The API has no pause state. Pausing archives the experiment, which cannot be reactivated.'
        : 'Archived experiments stop serving variants and cannot be reactivated.',
      execute: async approval => {
        await experimentsApi.archive(exp.id, approval);
      },
    });
  }

  // Create experiment form state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formTraffic, setFormTraffic] = useState(50);
  const [formVariants, setFormVariants] = useState<ExperimentVariant[]>([
    { name: 'Control', weight: 50, is_control: true, config: {} },
    { name: 'Treatment A', weight: 50, is_control: false, config: {} },
  ]);

  const createMutation = useMutation({
    mutationFn: () =>
      experimentsApi.create({
        name: formName,
        description: formDesc,
        trafficPercentage: formTraffic,
        variants: formVariants,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      setShowCreateModal(false);
      resetForm();
    },
  });

  const resetForm = () => {
    setFormName('');
    setFormDesc('');
    setFormTraffic(50);
    setFormVariants([
      { name: 'Control', weight: 50, is_control: true, config: {} },
      { name: 'Treatment A', weight: 50, is_control: false, config: {} },
    ]);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">A/B Experiments</h1>
          <p className="text-gray-text">Manage experiments and variant configurations for the frontend</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => refetch()} className="btn-secondary flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>New Experiment</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
            className={`glass-card p-4 text-left transition-all ${statusFilter === status ? 'ring-2 ring-primary' : ''}`}
          >
            <p className="text-gray-text text-sm mb-1">{status}</p>
            <p className="text-xl font-bold text-white">
              {experiments?.filter((e) => e.status === status).length ?? 0}
            </p>
          </button>
        ))}
      </div>

      {/* Experiment List */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-gray-text">Loading experiments...</p>
          </div>
        ) : !experiments || experiments.length === 0 ? (
          <div className="p-12 text-center">
            <FlaskConical className="w-12 h-12 text-gray-text mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No experiments yet</h3>
            <p className="text-gray-text mb-6">Create your first A/B test experiment</p>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              Create Experiment
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-border">
            {experiments.map((exp) => (
              <div key={exp.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <FlaskConical className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="text-white font-medium">{exp.name}</h3>
                      <p className="text-gray-text text-sm">{exp.description || 'No description'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[exp.status]}`}>
                      {exp.status}
                    </span>
                    <div className="flex items-center space-x-1 text-gray-text text-sm">
                      <Users className="w-4 h-4" />
                      <span>{exp.traffic_percentage}%</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {exp.status === 'DRAFT' && (
                        <button
                          onClick={() => requestActivate(exp)}
                          className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                          title="Activate"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      {exp.status === 'ACTIVE' && (
                        <button
                          onClick={() => requestArchive(exp, true)}
                          className="p-2 text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-colors"
                          title="Pause (archives the experiment)"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      )}
                      {exp.status !== 'ARCHIVED' && (
                        <button
                          onClick={() => requestArchive(exp, false)}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
                        className="p-2 text-gray-text hover:text-white rounded-lg transition-colors"
                      >
                        {expandedId === exp.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {expandedId === exp.id && (
                  <div className="mt-4 pt-4 border-t border-gray-border">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-text">Created:</span>{' '}
                        <span className="text-white">{new Date(exp.created_at).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-text">Updated:</span>{' '}
                        <span className="text-white">{new Date(exp.updated_at).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-text">Target:</span>{' '}
                        <span className="text-white">{exp.target_audience || 'All users'}</span>
                      </div>
                      <div>
                        <span className="text-gray-text">Tags:</span>{' '}
                        <span className="text-white">{exp.tags?.join(', ') || 'None'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Experiment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-spring-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Create Experiment</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-text hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-text mb-1">Name</label>
                  <input value={formName} onChange={(e) => setFormName(e.target.value)} className="input w-full" placeholder="Experiment name" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-text mb-1">Description</label>
                  <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="input w-full" rows={2} placeholder="What are you testing?" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-text mb-1">
                    Traffic Allocation: {formTraffic}%
                  </label>
                  <input type="range" min={0} max={100} value={formTraffic} onChange={(e) => setFormTraffic(Number(e.target.value))} className="w-full" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-text">Variants</label>
                    <button
                      onClick={() => setFormVariants([...formVariants, { name: `Treatment ${String.fromCharCode(64 + formVariants.length)}`, weight: 0, is_control: false, config: {} }])}
                      className="text-primary text-sm hover:underline"
                    >
                      + Add Variant
                    </button>
                  </div>
                  <div className="space-y-3">
                    {formVariants.map((v, i) => (
                      <div key={i} className="bg-dark-lighter rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          <input
                            value={v.name}
                            onChange={(e) => {
                              const updated = [...formVariants];
                              updated[i] = { ...v, name: e.target.value };
                              setFormVariants(updated);
                            }}
                            className="input flex-1"
                            placeholder="Variant name"
                          />
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-text text-sm">Weight:</span>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={v.weight}
                              onChange={(e) => {
                                const updated = [...formVariants];
                                updated[i] = { ...v, weight: Number(e.target.value) };
                                setFormVariants(updated);
                              }}
                              className="input w-20"
                            />
                          </div>
                          {v.is_control && (
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">Control</span>
                          )}
                          {!v.is_control && formVariants.length > 2 && (
                            <button onClick={() => setFormVariants(formVariants.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="mt-2">
                          <label className="block text-xs text-gray-text mb-1">Config (JSON)</label>
                          <textarea
                            value={JSON.stringify(v.config, null, 2)}
                            onChange={(e) => {
                              try {
                                const updated = [...formVariants];
                                updated[i] = { ...v, config: JSON.parse(e.target.value) };
                                setFormVariants(updated);
                              } catch { /* ignore invalid JSON while typing */ }
                            }}
                            className="input w-full font-mono text-xs"
                            rows={3}
                            placeholder='{ "buttonColor": "blue" }'
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button onClick={() => setShowCreateModal(false)} className="btn-secondary">Cancel</button>
                  <button
                    onClick={() => createMutation.mutate()}
                    disabled={!formName || formVariants.length < 2}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Experiment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {approvalAction && (
        <ApprovalRequestModal
          operation={approvalAction.operation}
          targetId={approvalAction.targetId}
          targetLabel={approvalAction.targetLabel}
          title={approvalAction.title}
          description={approvalAction.description}
          execute={approvalAction.execute}
          onClose={() => setApprovalAction(null)}
          onCompleted={() => {
            queryClient.invalidateQueries({ queryKey: ['experiments'] });
            toast.success(`${approvalAction.title}: done`);
          }}
        />
      )}
    </div>
  );
}
